// pdfVectors.js — pull vector linework + text out of a pdf.js page.
// Runs on the main thread (needs the pdf.js page); output is plain typed arrays that can be
// transferred to the engine worker.
//
// Frame: "sheet units" = CSS px of page.getViewport({ scale: 1 }) — PDF points, origin top-left,
// y down, page rotation applied. The viewer renders at renderScale R, so viewer px = sheet units × R.
//
// TEXT IS NEVER GEOMETRY: showText ops are skipped entirely; text comes only from getTextContent()
// and is used for naming rooms, reading wall tags and the scale note — never as a boundary.

// pdf.js v4 constructPath sub-ops
const V4 = {
  moveTo: 13,
  lineTo: 14,
  curveTo: 15,
  curveTo2: 16,
  curveTo3: 17,
  closePath: 18,
  rectangle: 19,
};
// pdf.js v5 DrawOPS (path data inside constructPath)
const V5 = {
  moveTo: 0,
  lineTo: 1,
  curveTo: 2,
  quadraticCurveTo: 3,
  closePath: 4,
};

const dashOn = (arr) =>
  !!arr &&
  (Array.isArray(arr) || ArrayBuffer.isView(arr)) &&
  Array.from(arr).some((v) => v > 0);
export const SEG_STROKE = 1,
  SEG_FILL = 2,
  SEG_CURVE = 4,
  SEG_DASH = 8; // SEG_DASH: stroked with a dash pattern

const mul = (m, n) => [
  m[0] * n[0] + m[2] * n[1],
  m[1] * n[0] + m[3] * n[1],
  m[0] * n[2] + m[2] * n[3],
  m[1] * n[2] + m[3] * n[3],
  m[0] * n[4] + m[2] * n[5] + m[4],
  m[1] * n[4] + m[3] * n[5] + m[5],
];
const lumOf = (args) => {
  let r, g, b;
  if (typeof args?.[0] === "string" && args[0][0] === "#") {
    const h = args[0];
    r = parseInt(h.slice(1, 3), 16);
    g = parseInt(h.slice(3, 5), 16);
    b = parseInt(h.slice(5, 7), 16);
  } else if (args && args[0] !== undefined) {
    r = args[0];
    g = args[1];
    b = args[2];
  } else return 0;
  if (r <= 1 && g <= 1 && b <= 1 && (r % 1 || g % 1 || b % 1)) {
    r *= 255;
    g *= 255;
    b *= 255;
  }
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
};

// packed 0xRRGGBB of a colour operator's args (pdf.js gives "#rrggbb", 0–1 or 0–255 numbers, or one grey value)
const rgbOf = (args) => {
  let r, g, b;
  if (typeof args?.[0] === "string" && args[0][0] === "#") {
    const h = args[0];
    r = parseInt(h.slice(1, 3), 16);
    g = parseInt(h.slice(3, 5), 16);
    b = parseInt(h.slice(5, 7), 16);
  } else if (args && args.length >= 3) {
    r = args[0];
    g = args[1];
    b = args[2];
  } else if (args && args.length === 1 && typeof args[0] === "number") {
    r = g = b = args[0];
  } else return 0;
  if (r <= 1 && g <= 1 && b <= 1 && (r % 1 || g % 1 || b % 1)) {
    r *= 255;
    g *= 255;
    b *= 255;
  }
  const c = (v) => Math.max(0, Math.min(255, Math.round(Number(v) || 0)));
  return (c(r) << 16) | (c(g) << 8) | c(b);
};

// What a small closed outline is — the symbol a wall-type tag is drawn in.
//   1 circle · 2 square/box · 3 diamond · 4 hexagon · 5 oval / rounded box · 6 triangle · 7 octagon · 8 other
export const SYM = {
  circle: 1,
  square: 2,
  diamond: 3,
  hexagon: 4,
  oval: 5,
  triangle: 6,
  octagon: 7,
  other: 8,
};
export const SYM_NAME = [
  "",
  "circle",
  "square",
  "diamond",
  "hexagon",
  "oval",
  "triangle",
  "octagon",
  "other",
];
function symbolKind(p, curve) {
  let x0 = Infinity,
    y0 = Infinity,
    x1 = -Infinity,
    y1 = -Infinity;
  for (let i = 0; i < p.length; i += 2) {
    x0 = Math.min(x0, p[i]);
    x1 = Math.max(x1, p[i]);
    y0 = Math.min(y0, p[i + 1]);
    y1 = Math.max(y1, p[i + 1]);
  }
  const w = x1 - x0,
    h = y1 - y0,
    m = Math.max(w, h) || 1;
  if (curve.some(Boolean)) {
    // all curve → circle / ellipse; curves + straight sides → oval / rounded box (door tags, room tags)
    const straight = curve.filter((c) => !c).length;
    return straight <= 1 && Math.min(w, h) >= m * 0.8 ? SYM.circle : SYM.oval;
  }
  // corners: drop repeated points and points on a straight run
  const pts = [];
  for (let i = 0; i < p.length; i += 2) {
    const q = pts[pts.length - 1];
    if (!q || Math.hypot(p[i] - q[0], p[i + 1] - q[1]) > m * 0.02)
      pts.push([p[i], p[i + 1]]);
  }
  if (
    pts.length > 2 &&
    Math.hypot(
      pts[0][0] - pts[pts.length - 1][0],
      pts[0][1] - pts[pts.length - 1][1],
    ) <=
      m * 0.02
  )
    pts.pop();
  const cor = [];
  for (let i = 0; i < pts.length; i++) {
    const a = pts[(i - 1 + pts.length) % pts.length],
      b = pts[i],
      c = pts[(i + 1) % pts.length];
    const ux = b[0] - a[0],
      uy = b[1] - a[1],
      vx = c[0] - b[0],
      vy = c[1] - b[1];
    const cr =
      Math.abs(ux * vy - uy * vx) /
      (Math.hypot(ux, uy) * Math.hypot(vx, vy) || 1);
    if (cr > 0.2) cor.push(b);
  }
  const n = cor.length;
  if (n === 3) return SYM.triangle;
  if (n === 4) {
    // sides along the axes = box; sides at 45° = diamond
    let diag = 0;
    for (let i = 0; i < 4; i++) {
      const a = cor[i],
        b = cor[(i + 1) % 4];
      const an = Math.abs(Math.atan2(b[1] - a[1], b[0] - a[0])) % (Math.PI / 2);
      if (an > 0.35 && an < 1.22) diag++;
    }
    return diag >= 3 ? SYM.diamond : SYM.square;
  }
  if (n === 6) return SYM.hexagon;
  if (n === 8) return SYM.octagon;
  if (n > 10) return Math.min(w, h) >= m * 0.8 ? SYM.circle : SYM.oval; // a circle drawn as many short lines
  return SYM.other;
}

class Grow {
  constructor(T, n = 1 << 16) {
    this.T = T;
    this.a = new T(n);
    this.n = 0;
  }
  push(...v) {
    if (this.n + v.length > this.a.length) {
      const b = new this.T(Math.max(this.a.length * 2, this.n + v.length));
      b.set(this.a);
      this.a = b;
    }
    for (const x of v) this.a[this.n++] = x;
  }
  out() {
    return this.a.slice(0, this.n);
  }
}

export async function extractSheet(pdfjsLib, page) {
  const OPS = pdfjsLib.OPS;
  const vp = page.getViewport({ scale: 1 });
  const VT = vp.transform;
  // Annotations (Bluebeam / Acrobat mark-ups, stamps, someone's old takeoff) are NOT wall geometry — but their
  // lines are often the leader arrows of wall-type tags and callouts. They're read into a separate list.
  const withAnnots = pdfjsLib.AnnotationMode
    ? { annotationMode: pdfjsLib.AnnotationMode.ENABLE }
    : undefined;
  const [ol, tc] = await Promise.all([
    page.getOperatorList(withAnnots),
    page.getTextContent(),
  ]);
  const annSegs = []; // straight segments drawn by annotations [x1,y1,x2,y2,…] — leaders only
  let annotDepth = 0;

  const segs = new Grow(Float32Array),
    segW = new Grow(Float32Array),
    segF = new Grow(Uint8Array),
    segSub = new Grow(Int32Array),
    segL = new Grow(Uint8Array);
  const segC = new Grow(Uint32Array); // colour of each segment (0xRRGGBB) — pre-coloured walls / fire-rating overlays
  const smallKind = []; // symbol kind (SYM) of each smallShapes box
  const subClosed = new Grow(Uint8Array),
    subCurve = new Grow(Uint8Array),
    subSize = new Grow(Float32Array),
    subOrtho = new Grow(Uint8Array);
  const fills = []; // [{subs:[Float32Array], eo, lum}] non-white filled paths (wall poche candidates)
  const smallShapes = []; // closed subpath bboxes (tag symbols, bubbles) [x0,y0,x1,y1]
  const rects = []; // closed straight-sided orthogonal subpaths < 1in on paper (posts, columns, pilasters, tag boxes) [x0,y0,x1,y1]
  let imageArea = 0;

  let st = {
    ctm: [1, 0, 0, 1, 0, 0],
    lw: 1,
    fillLum: 0,
    strokeLum: 0,
    fillRGB: 0,
    strokeRGB: 0,
    dash: false,
  };
  const stack = [];
  let sub = []; // current path: array of subpaths {pts:[x,y,...] (device), closed, curve:[bool per seg]}

  const dev = (x, y) => {
    const m = mul(VT, st.ctm);
    return [m[0] * x + m[2] * y + m[4], m[1] * x + m[3] * y + m[5]];
  };
  const scaleOf = () => {
    const m = mul(VT, st.ctm);
    return Math.sqrt(Math.abs(m[0] * m[3] - m[1] * m[2])) || 1;
  };

  let cur = null;
  const start = (x, y) => {
    cur = { pts: [...dev(x, y)], closed: false, curve: [] };
    sub.push(cur);
  };
  const lineTo = (x, y, curve = false) => {
    if (!cur) start(x, y);
    const p = dev(x, y);
    cur.pts.push(p[0], p[1]);
    cur.curve.push(curve);
  };
  const cubic = (x0, y0, x1, y1, x2, y2, x3, y3) => {
    const N = 8;
    for (let i = 1; i <= N; i++) {
      const t = i / N,
        u = 1 - t;
      lineTo(
        u * u * u * x0 +
          3 * u * u * t * x1 +
          3 * u * t * t * x2 +
          t * t * t * x3,
        u * u * u * y0 +
          3 * u * u * t * y1 +
          3 * u * t * t * y2 +
          t * t * t * y3,
        true,
      );
    }
  };
  let last = [0, 0],
    first = [0, 0];
  const close = () => {
    if (cur && cur.pts.length >= 4) {
      lineTo(first[0], first[1]);
      cur.closed = true;
    }
    last = first;
    cur = null;
  };

  function buildV4(ops, a) {
    let j = 0;
    for (const op of ops) {
      switch (op) {
        case V4.moveTo:
          last = first = [a[j], a[j + 1]];
          start(a[j], a[j + 1]);
          j += 2;
          break;
        case V4.lineTo:
          lineTo(a[j], a[j + 1]);
          last = [a[j], a[j + 1]];
          j += 2;
          break;
        case V4.curveTo:
          cubic(
            last[0],
            last[1],
            a[j],
            a[j + 1],
            a[j + 2],
            a[j + 3],
            a[j + 4],
            a[j + 5],
          );
          last = [a[j + 4], a[j + 5]];
          j += 6;
          break;
        case V4.curveTo2:
          cubic(
            last[0],
            last[1],
            last[0],
            last[1],
            a[j],
            a[j + 1],
            a[j + 2],
            a[j + 3],
          );
          last = [a[j + 2], a[j + 3]];
          j += 4;
          break;
        case V4.curveTo3:
          cubic(
            last[0],
            last[1],
            a[j],
            a[j + 1],
            a[j + 2],
            a[j + 3],
            a[j + 2],
            a[j + 3],
          );
          last = [a[j + 2], a[j + 3]];
          j += 4;
          break;
        case V4.closePath:
          close();
          break;
        case V4.rectangle: {
          const [x, y, w, h] = [a[j], a[j + 1], a[j + 2], a[j + 3]];
          j += 4;
          first = [x, y];
          start(x, y);
          lineTo(x + w, y);
          lineTo(x + w, y + h);
          lineTo(x, y + h);
          close();
          break;
        }
        default:
          break;
      }
    }
  }
  function buildV5(d) {
    for (let j = 0; j < d.length; ) {
      switch (d[j++]) {
        case V5.moveTo:
          last = first = [d[j], d[j + 1]];
          start(d[j], d[j + 1]);
          j += 2;
          break;
        case V5.lineTo:
          lineTo(d[j], d[j + 1]);
          last = [d[j], d[j + 1]];
          j += 2;
          break;
        case V5.curveTo:
          cubic(
            last[0],
            last[1],
            d[j],
            d[j + 1],
            d[j + 2],
            d[j + 3],
            d[j + 4],
            d[j + 5],
          );
          last = [d[j + 4], d[j + 5]];
          j += 6;
          break;
        case V5.quadraticCurveTo: {
          const [cx, cy, x, y] = [d[j], d[j + 1], d[j + 2], d[j + 3]];
          j += 4;
          cubic(
            last[0],
            last[1],
            last[0] + (2 / 3) * (cx - last[0]),
            last[1] + (2 / 3) * (cy - last[1]),
            x + (2 / 3) * (cx - x),
            y + (2 / 3) * (cy - y),
            x,
            y,
          );
          last = [x, y];
          break;
        }
        case V5.closePath:
          close();
          break;
        default:
          return; // unknown — stop parsing this path
      }
    }
  }

  function paint(stroke, fill, eo = false) {
    if (annotDepth > 0) {
      // mark-up: keep its straight strokes as possible leaders, nothing else
      if (stroke)
        for (const sp of sub) {
          const p = sp.pts;
          for (let i = 0; i + 3 < p.length; i += 2)
            if (!sp.curve[i / 2])
              annSegs.push(p[i], p[i + 1], p[i + 2], p[i + 3]);
        }
      sub = [];
      cur = null;
      return;
    }
    const w = st.lw * scaleOf();
    const compound = [];
    for (const sp of sub) {
      const p = sp.pts;
      if (p.length < 4) continue;
      let flags = (stroke ? SEG_STROKE : 0) | (fill ? SEG_FILL : 0);
      const subId = subClosed.n;
      // (CAD exports often draw a tag diamond / hexagon as an open polyline that ends where it started)
      if (
        !sp.closed &&
        p.length >= 8 &&
        Math.hypot(p[0] - p[p.length - 2], p[1] - p[p.length - 1]) < 0.35
      )
        sp.closed = true;
      {
        let x0 = Infinity,
          y0 = Infinity,
          x1 = -Infinity,
          y1 = -Infinity;
        for (let i = 0; i < p.length; i += 2) {
          x0 = Math.min(x0, p[i]);
          x1 = Math.max(x1, p[i]);
          y0 = Math.min(y0, p[i + 1]);
          y1 = Math.max(y1, p[i + 1]);
        }
        subClosed.push(sp.closed || fill ? 1 : 0);
        subCurve.push(sp.curve.some(Boolean) ? 1 : 0);
        subSize.push(Math.max(x1 - x0, y1 - y0));
        let ortho = 1;
        for (let i = 0; i + 3 < p.length; i += 2) {
          const dx = Math.abs(p[i + 2] - p[i]),
            dy = Math.abs(p[i + 3] - p[i + 1]);
          if (Math.min(dx, dy) > Math.max(dx, dy) * 0.02) {
            ortho = 0;
            break;
          }
        }
        subOrtho.push(ortho);
      }
      for (let i = 0; i + 3 < p.length; i += 2) {
        const dx = p[i + 2] - p[i],
          dy = p[i + 3] - p[i + 1];
        if (dx * dx + dy * dy < 1e-6) continue;
        segs.push(p[i], p[i + 1], p[i + 2], p[i + 3]);
        segW.push(stroke ? w : 0);
        segF.push(
          flags |
            (sp.curve[i / 2] ? SEG_CURVE : 0) |
            (stroke && st.dash ? SEG_DASH : 0),
        );
        segSub.push(subId);
        segL.push(Math.round((stroke ? st.strokeLum : st.fillLum) * 255));
        segC.push(stroke ? st.strokeRGB : st.fillRGB);
      }
      if (sp.closed || fill) {
        let x0 = Infinity,
          y0 = Infinity,
          x1 = -Infinity,
          y1 = -Infinity;
        for (let i = 0; i < p.length; i += 2) {
          x0 = Math.min(x0, p[i]);
          x1 = Math.max(x1, p[i]);
          y0 = Math.min(y0, p[i + 1]);
          y1 = Math.max(y1, p[i + 1]);
        }
        // (a white wipeout behind a label is a mask, not a symbol outline)
        const wipeout = fill && !stroke && st.fillLum > 0.93;
        if (Math.max(x1 - x0, y1 - y0) < 72 && !wipeout) {
          smallShapes.push(x0, y0, x1, y1);
          smallKind.push(symbolKind(p, sp.curve));
        }
        if (
          Math.max(x1 - x0, y1 - y0) < 72 &&
          !sp.curve.some(Boolean) &&
          subOrtho.a[subId] &&
          p.length <= 12
        )
          rects.push(x0, y0, x1, y1);
        if (fill && p.length >= 6) compound.push(Float32Array.from(p));
      }
    }
    if (compound.length && st.fillLum < 0.97)
      fills.push({ subs: compound, eo, lum: st.fillLum });
    sub = [];
    cur = null;
  }

  const F = ol.fnArray,
    A = ol.argsArray;
  for (let i = 0; i < F.length; i++) {
    const fn = F[i],
      args = A[i];
    switch (fn) {
      case OPS.save:
        stack.push({ ...st, ctm: st.ctm.slice() });
        break;
      case OPS.restore:
        if (stack.length) st = stack.pop();
        break;
      case OPS.transform:
        st.ctm = mul(st.ctm, args);
        break;
      case OPS.paintFormXObjectBegin:
        stack.push({ ...st, ctm: st.ctm.slice() });
        if (Array.isArray(args?.[0]) || ArrayBuffer.isView(args?.[0]))
          st.ctm = mul(st.ctm, Array.from(args[0]));
        break;
      case OPS.paintFormXObjectEnd:
        if (stack.length) st = stack.pop();
        break;
      case OPS.beginAnnotation: {
        // args: [id, rect, transform, matrix, …] — the appearance stream is drawn in transform × matrix space
        stack.push({ ...st, ctm: st.ctm.slice() });
        annotDepth++;
        const tr = args?.[2],
          mx = args?.[3];
        if (tr && tr.length === 6) st.ctm = mul(st.ctm, Array.from(tr));
        if (mx && mx.length === 6) st.ctm = mul(st.ctm, Array.from(mx));
        break;
      }
      case OPS.endAnnotation:
        if (stack.length) st = stack.pop();
        annotDepth = Math.max(0, annotDepth - 1);
        break;
      case OPS.setLineWidth:
        st.lw = args[0];
        break;
      // a dash pattern (hidden / overhead lines, turning circles): kept on the segments so they are never read as
      // walls or door swings
      case OPS.setDash:
        st.dash = dashOn(args?.[0]);
        break;
      case OPS.setGState:
        for (const [k, v] of args?.[0] || []) {
          if (k === "LW") st.lw = v;
          if (k === "D") st.dash = dashOn(Array.isArray(v) ? v[0] : null);
        }
        break;
      case OPS.setFillRGBColor:
      case OPS.setFillGray:
      case OPS.setFillColor:
        st.fillLum = lumOf(args);
        st.fillRGB = rgbOf(args);
        break;
      case OPS.setStrokeRGBColor:
      case OPS.setStrokeGray:
      case OPS.setStrokeColor:
        st.strokeLum = lumOf(args);
        st.strokeRGB = rgbOf(args);
        break;
      case OPS.constructPath: {
        if (typeof args[0] === "number") {
          // pdf.js v5: [paintOp, [data], minMax]
          const d = args[1]?.[0];
          if (d) buildV5(d);
          const op = args[0];
          if (op === OPS.stroke || op === OPS.closeStroke) paint(true, false);
          else if (op === OPS.fill || op === OPS.eoFill)
            paint(false, true, op === OPS.eoFill);
          else if (
            op === OPS.fillStroke ||
            op === OPS.eoFillStroke ||
            op === OPS.closeFillStroke ||
            op === OPS.closeEOFillStroke
          )
            paint(
              true,
              true,
              op === OPS.eoFillStroke || op === OPS.closeEOFillStroke,
            );
          else {
            sub = [];
            cur = null;
          }
        } else buildV4(args[0], args[1]);
        break;
      }
      case OPS.stroke:
      case OPS.closeStroke:
        paint(true, false);
        break;
      case OPS.fill:
        paint(false, true, false);
        break;
      case OPS.eoFill:
        paint(false, true, true);
        break;
      case OPS.fillStroke:
      case OPS.closeFillStroke:
        paint(true, true, false);
        break;
      case OPS.eoFillStroke:
      case OPS.closeEOFillStroke:
        paint(true, true, true);
        break;
      case OPS.endPath:
        sub = [];
        cur = null;
        break;
      case OPS.paintImageXObject:
      case OPS.paintInlineImageXObject:
      case OPS.paintImageMaskXObject: {
        const m = mul(VT, st.ctm);
        imageArea += Math.abs(m[0] * m[3] - m[1] * m[2]);
        break;
      }
      default:
        break;
    }
  }

  // text: only for names / tags / scale — never for geometry
  const text = [];
  for (const it of tc.items || []) {
    const s = (it.str || "").trim();
    if (!s) continue;
    const t = pdfjsLib.Util.transform(VT, it.transform);
    const an = Math.hypot(t[2], t[3]) || 1,
      dn = Math.hypot(t[0], t[1]) || 1;
    const h = it.height || an;
    const w = it.width || s.length * h * 0.55;
    const ax = (t[2] / an) * h,
      ay = (t[3] / an) * h; // ascent vector (device)
    const dx = t[0] / dn,
      dy = t[1] / dn; // run direction
    const xs = [t[4], t[4] + w * dx, t[4] + ax, t[4] + w * dx + ax];
    const ys = [t[5], t[5] + w * dy, t[5] + ay, t[5] + w * dy + ay];
    text.push({
      str: s,
      x0: Math.min(...xs),
      y0: Math.min(...ys),
      x1: Math.max(...xs),
      y1: Math.max(...ys),
      h,
      rot: Math.round((Math.atan2(dy, dx) * 180) / Math.PI),
    });
  }

  // join abutting runs on one baseline ("P-" + "2" → "P-2", "13" + "4A" → "134A")
  const joined = [];
  for (const t of text) {
    const prev = joined[joined.length - 1];
    if (prev && prev.rot === t.rot && t.rot === 0) {
      const h = Math.max(prev.y1 - prev.y0, t.y1 - t.y0);
      const sameLine =
        Math.abs(prev.y1 - t.y1) < h * 0.25 &&
        Math.abs(prev.y0 - t.y0) < h * 0.4;
      const gap = t.x0 - prev.x1;
      if (sameLine && gap > -h * 0.3 && gap < h * 0.35) {
        prev.str += (gap > h * 0.2 ? " " : "") + t.str;
        prev.x1 = Math.max(prev.x1, t.x1);
        prev.y0 = Math.min(prev.y0, t.y0);
        prev.y1 = Math.max(prev.y1, t.y1);
        continue;
      }
    }
    joined.push({ ...t });
  }
  text.length = 0;
  text.push(...joined);

  // Mark-up / text-box annotations often carry real drawing text (wall-type numbers in tag symbols,
  // room names, dimensions) even though their linework is ignored. Short ones join the text layer.
  try {
    for (const a of await page.getAnnotations()) {
      const s = (a.contentsObj?.str || a.richText?.str || "")
        .replace(/\s+/g, " ")
        .trim();
      if (!s || s.length > 60 || !a.rect) continue;
      const [x0, y0, x1, y1] = vp.convertToViewportRectangle(a.rect);
      const X0 = Math.min(x0, x1),
        Y0 = Math.min(y0, y1),
        X1 = Math.max(x0, x1),
        Y1 = Math.max(y0, y1);
      if (
        text.some(
          (t) =>
            t.str === s && Math.abs(t.x0 - X0) < 4 && Math.abs(t.y0 - Y0) < 4,
        )
      )
        continue;
      text.push({
        str: s,
        x0: X0,
        y0: Y0,
        x1: X1,
        y1: Y1,
        h: Math.min(Y1 - Y0, 14),
        rot: 0,
        annot: true,
      });
    }
  } catch {
    /* annotations are optional */
  }

  const segArr = segs.out(),
    flagArr = segF.out();
  markPolylineArcs(segArr, flagArr);

  return {
    width: vp.width,
    height: vp.height,
    segs: segArr,
    segW: segW.out(),
    segF: flagArr,
    segSub: segSub.out(),
    segL: segL.out(),
    segC: segC.out(),
    smallKind: Uint8Array.from(smallKind),
    subClosed: subClosed.out(),
    subCurve: subCurve.out(),
    subSize: subSize.out(),
    subOrtho: subOrtho.out(),
    fills,
    smallShapes: Float32Array.from(smallShapes),
    rects: Float32Array.from(rects),
    annSegs: Float32Array.from(annSegs),
    text,
    imageArea,
  };
}

// CAD exports often write a door swing / round fixture as a chain of short straight lines (sometimes
// each one its own path). Chains of ≥4 short segments that keep turning the same way are arcs: flag
// them as curves so they are never mistaken for walls, and so door swings can be recognised.
function markPolylineArcs(S, F, maxLen = 20) {
  const n = S.length >> 2;
  const key = (x, y) => `${Math.round(x * 4)},${Math.round(y * 4)}`;
  const ends = new Map();
  const short = new Uint8Array(n);
  for (let i = 0; i < n; i++) {
    const L = Math.hypot(S[i * 4 + 2] - S[i * 4], S[i * 4 + 3] - S[i * 4 + 1]);
    if (L > maxLen || L < 0.2) continue;
    short[i] = 1;
    for (const k of [
      key(S[i * 4], S[i * 4 + 1]),
      key(S[i * 4 + 2], S[i * 4 + 3]),
    ]) {
      if (!ends.has(k)) ends.set(k, []);
      ends.get(k).push(i);
    }
  }
  const parent = new Int32Array(n);
  for (let i = 0; i < n; i++) parent[i] = i;
  const find = (x) => {
    while (parent[x] !== x) {
      parent[x] = parent[parent[x]];
      x = parent[x];
    }
    return x;
  };
  const turnSum = new Float64Array(n),
    turnAbs = new Float64Array(n);
  const dir = (i, fromKey) => {
    // direction pointing AWAY from the shared point
    const a = key(S[i * 4], S[i * 4 + 1]) === fromKey;
    const dx = a ? S[i * 4 + 2] - S[i * 4] : S[i * 4] - S[i * 4 + 2],
      dy = a ? S[i * 4 + 3] - S[i * 4 + 1] : S[i * 4 + 1] - S[i * 4 + 3];
    const L = Math.hypot(dx, dy) || 1;
    return [dx / L, dy / L];
  };
  for (const [k, list] of ends) {
    if (list.length !== 2) continue; // only simple chain joints
    const [i, j] = list;
    const [ax, ay] = dir(i, k),
      [bx, by] = dir(j, k);
    // turning angle = deviation from straight continuation (incoming = -a)
    const cross = -ax * by + ay * bx,
      dot = -ax * bx - ay * by;
    const turn = Math.atan2(cross, dot);
    const deg = (Math.abs(turn) * 180) / Math.PI;
    if (deg < 1.5 || deg > 35) continue;
    const ri = find(i),
      rj = find(j);
    if (ri !== rj) {
      parent[rj] = ri;
      turnSum[ri] += turnSum[rj];
      turnAbs[ri] += turnAbs[rj];
    }
    // sign is only consistent along an oriented chain; accumulate both for a coherence test
    turnSum[find(i)] += turn;
    turnAbs[find(i)] += Math.abs(turn);
  }
  const size = new Map();
  for (let i = 0; i < n; i++)
    if (short[i]) {
      const r = find(i);
      size.set(r, (size.get(r) || 0) + 1);
    }
  for (let i = 0; i < n; i++) {
    if (!short[i]) continue;
    const r = find(i);
    if ((size.get(r) || 0) >= 4 && turnAbs[r] > 0.35) F[i] |= SEG_CURVE;
  }
}
